import {
  useAutofillMeterUsages,
  useCreateMeterUsage,
  useDeleteMeterUsage,
  useUpdateMeterUsage
} from '@services/settlements/mutations/useMeterUsageMutations'
import type { SettlementDetail } from '@services/settlements/types'
import { Gauge, Trash2, Wand2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { useConfirm } from '@/components/ui/confirm-dialog'

import { MaybeCard } from './SectionCard'
import { UnitInput } from './UnitInput'
import { BLOCK_UNITS, quantity, unitLabel } from './format'

/** Only metered blocks: heating and other items are annual amounts. */
const USAGE_BLOCKS = ['HOT_WATER', 'COLD_WATER', 'ELECTRICITY'] as const
type UsageBlock = (typeof USAGE_BLOCKS)[number]

/**
 * Meter readings for the period. Two rows for the same block mean the meter
 * was swapped: their consumptions add up instead of subtracting across the
 * swap.
 */
export function SettlementMeterUsageCard({
  settlement,
  readOnly,
  blocks = USAGE_BLOCKS,
  embedded = false
}: {
  settlement: SettlementDetail
  readOnly: boolean
  /** Restrict to the blocks of the current wizard step. */
  blocks?: readonly UsageBlock[]
  /** Render without the card chrome, inside a step. */
  embedded?: boolean
}) {
  const { t } = useTranslation('settlement')
  const { confirm, dialog } = useConfirm()

  const autofill = useAutofillMeterUsages()
  const create = useCreateMeterUsage()
  const update = useUpdateMeterUsage()
  const remove = useDeleteMeterUsage()

  const [block, setBlock] = useState<UsageBlock>(blocks[0] ?? 'HOT_WATER')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')

  const usages = settlement.meter_usages.filter((usage) =>
    (blocks as readonly string[]).includes(usage.block)
  )

  const onDelete = async (usageId: string) => {
    if (await confirm({ title: t('usage.delete_confirm'), destructive: true })) {
      remove.mutate({ id: settlement.id, usageId })
    }
  }

  const content = (
    <MaybeCard
      embedded={embedded}
      icon={Gauge}
      title={t('usage.title')}
      action={
        !readOnly && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => autofill.mutate(settlement.id)}
            disabled={autofill.isPending}
          >
            <Wand2 className="mr-1.5 h-4 w-4" />
            {t('usage.autofill')}
          </Button>
        )
      }
    >
      {usages.length === 0 ? (
        <p className="m-0 mb-4 text-sm text-muted-foreground">
          {t('usage.empty')}
        </p>
      ) : (
        <div className="space-y-2">
          {usages.map((usage) => (
            <div
              key={usage.id}
              className="grid grid-cols-2 items-center gap-3 rounded-xl border border-ink/[0.08] px-3 py-2 sm:grid-cols-[150px_1fr_1fr_110px_auto]"
            >
              <div>
                <p className="m-0 text-sm font-medium text-ink">
                  {t(`blocks.${usage.block}`, usage.block)}
                </p>
                {usage.meter_serial_number && (
                  <p className="m-0 text-xs text-muted-foreground">
                    {usage.meter_serial_number}
                  </p>
                )}
              </div>
              <UnitInput
                unit={unitLabel(BLOCK_UNITS[usage.block]) ?? undefined}
                defaultValue={usage.start_reading}
                inputMode="decimal"
                disabled={readOnly}
                onBlur={(event) =>
                  event.target.value !== usage.start_reading &&
                  update.mutate({
                    id: settlement.id,
                    usageId: usage.id,
                    body: { start_reading: event.target.value }
                  })
                }
              />
              <UnitInput
                unit={unitLabel(BLOCK_UNITS[usage.block]) ?? undefined}
                defaultValue={usage.end_reading}
                inputMode="decimal"
                disabled={readOnly}
                onBlur={(event) =>
                  event.target.value !== usage.end_reading &&
                  update.mutate({
                    id: settlement.id,
                    usageId: usage.id,
                    body: { end_reading: event.target.value }
                  })
                }
              />
              <span className="text-right text-sm tabular-nums text-muted-foreground">
                {quantity(usage.consumption, BLOCK_UNITS[usage.block])}
              </span>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(usage.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Heat for hot water has no row of its own on purpose: its quantity
          is the hot water consumption, priced in EUR/m3. */}
      {blocks.includes('HOT_WATER') && (
        <p className="mt-4 m-0 text-xs text-muted-foreground">
          {t('usage.heat_water_hint')}
        </p>
      )}

      {/* A settlement does not need a registered meter: a period can be
          settled from two numbers off the management company's statement. */}
      {!readOnly && (
        <div className="mt-4 flex flex-wrap gap-2">
          <div className="w-44">
            <Combobox
              value={block}
              searchable={false}
              options={blocks.map((value) => ({
                value,
                label: t(`blocks.${value}`)
              }))}
              onChange={(value) =>
                setBlock((value as UsageBlock) ?? blocks[0] ?? 'HOT_WATER')
              }
            />
          </div>
          <UnitInput
            className="w-36"
            unit={unitLabel(BLOCK_UNITS[block]) ?? undefined}
            inputMode="decimal"
            placeholder={t('usage.start_reading')}
            value={start}
            onChange={(event) => setStart(event.target.value)}
          />
          <UnitInput
            className="w-36"
            unit={unitLabel(BLOCK_UNITS[block]) ?? undefined}
            inputMode="decimal"
            placeholder={t('usage.end_reading')}
            value={end}
            onChange={(event) => setEnd(event.target.value)}
          />
          <Button
            variant="outline"
            disabled={!start || !end || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  id: settlement.id,
                  body: {
                    block,
                    start_reading: start,
                    end_reading: end
                  }
                },
                {
                  onSuccess: () => {
                    setStart('')
                    setEnd('')
                  }
                }
              )
            }
          >
            {t('usage.add')}
          </Button>
        </div>
      )}

    </MaybeCard>
  )

  return (
    <>
      {content}
      {dialog}
    </>
  )
}

